<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ClinicalCrmAPI from 'dashboard/api/clinicalCrm';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const emit = defineEmits(['close', 'select']);
const { t } = useI18n();
const quickReplies = ref([]);
const query = ref('');
const isLoading = ref(false);

const filteredQuickReplies = computed(() => {
  const needle = query.value.trim().toLocaleLowerCase();
  if (!needle) return quickReplies.value;

  return quickReplies.value.filter(quickReply =>
    [
      quickReply.title,
      quickReply.content,
      quickReply.mediaAsset?.title,
      quickReply.mediaAsset?.description,
    ]
      .filter(Boolean)
      .some(value => value.toLocaleLowerCase().includes(needle))
  );
});

const fetchQuickReplies = async () => {
  isLoading.value = true;
  try {
    const { data } = await ClinicalCrmAPI.getQuickReplies();
    quickReplies.value = data;
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchQuickReplies);
</script>

<template>
  <woot-modal show :on-close="() => emit('close')">
    <div class="flex flex-col max-h-[80vh] p-6">
      <div class="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ t('CLINICAL_CRM.QUICK_REPLIES.PICKER.TITLE') }}
          </h2>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('CLINICAL_CRM.QUICK_REPLIES.PICKER.DESCRIPTION') }}
          </p>
        </div>
        <Button icon="i-lucide-x" slate @click="emit('close')" />
      </div>

      <Input
        v-model="query"
        type="search"
        :placeholder="t('CLINICAL_CRM.QUICK_REPLIES.PICKER.SEARCH')"
        class="mb-4"
      />

      <div
        v-if="isLoading"
        class="grid min-h-48 place-items-center text-sm text-n-slate-11"
      >
        {{ t('CLINICAL_CRM.QUICK_REPLIES.LOADING') }}
      </div>
      <div
        v-else-if="!filteredQuickReplies.length"
        class="grid min-h-48 place-items-center text-sm text-n-slate-11"
      >
        {{ t('CLINICAL_CRM.QUICK_REPLIES.PICKER.EMPTY') }}
      </div>
      <div v-else class="grid gap-3 overflow-auto">
        <article
          v-for="quickReply in filteredQuickReplies"
          :key="quickReply.id"
          class="flex gap-3 p-3 border rounded-xl border-n-weak bg-n-alpha-1"
        >
          <img
            v-if="quickReply.mediaAsset?.kind === 'image'"
            :src="quickReply.mediaAsset.fileUrl"
            :alt="quickReply.mediaAsset.title"
            class="object-cover flex-shrink-0 w-16 h-16 rounded-lg bg-n-slate-3"
          />
          <div
            v-else-if="quickReply.mediaAsset"
            class="grid flex-shrink-0 w-16 h-16 rounded-lg place-items-center bg-n-slate-3 text-n-slate-11"
          >
            <span class="i-lucide-paperclip size-6" />
          </div>
          <div class="min-w-0 flex-1">
            <h3 class="truncate text-sm font-medium text-n-slate-12">
              {{ quickReply.title }}
            </h3>
            <p
              v-if="quickReply.content"
              class="mt-0.5 text-xs whitespace-pre-line text-n-slate-11 line-clamp-3"
            >
              {{ quickReply.content }}
            </p>
            <p
              v-if="quickReply.mediaAsset"
              class="mt-1 text-xs text-n-slate-10"
            >
              {{
                t('CLINICAL_CRM.QUICK_REPLIES.PICKER.ATTACHMENT', {
                  title: quickReply.mediaAsset.title,
                })
              }}
            </p>
            <Button
              :label="t('CLINICAL_CRM.QUICK_REPLIES.PICKER.INSERT')"
              size="sm"
              class="mt-2"
              @click="emit('select', quickReply)"
            />
          </div>
        </article>
      </div>
    </div>
  </woot-modal>
</template>
